import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormInput,
  FormPrefixedInput,
  FormSelect,
  DialogFooter,
  Button,
  useMultiStepForm,
} from "web-design-system";
import {
  useInvestorContactInfo,
  useCreateInvestorContactInfo,
  useUpdateInvestorContactInfo,
  useUpdateClient,
} from "creso-core/hooks";
import { ContactDetailsSchema } from "creso-core/zod";
import { BELONGS_TO } from "creso-core/constants";
import { parsePhoneForMarketingDisplay } from "creso-core/utils";
import { InfoIcon } from "lucide-react";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";

/** `minor`: the guardian's mobile and email are used by default */
export function ContactDetails({ minor = false }: { minor?: boolean }) {
  const defaultBelongsTo = minor ? ("guardian" as const) : ("self" as const);
  const { client } = useClientOnboarding();
  const { nextStep, previousStep } = useMultiStepForm();

  const { data: details } = useInvestorContactInfo(
    client?.investment_account ?? ""
  );

  const { mutateAsync: updateClient } = useUpdateClient(client?.id ?? 0);

  const { mutateAsync: createContactInfo } = useCreateInvestorContactInfo();

  const { mutateAsync: updateContactInfo } = useUpdateInvestorContactInfo();

  // NRIs can have a non-Indian number, so the ISD comes from the saved details
  // or the client's phone number
  const clientPhone = parsePhoneForMarketingDisplay(client?.phone_number ?? "");
  const isd = details?.isd || clientPhone.callingCodeLabel;

  const form = useForm({
    resolver: zodResolver(ContactDetailsSchema),
    values: {
      number: details?.number
        ? details.number.replace(isd, "")
        : clientPhone.nationalNumber,
      phone_belongs_to: details?.phone_belongs_to ?? defaultBelongsTo,
      email: (details?.email || client?.email) ?? "",
      email_belongs_to: details?.email_belongs_to ?? defaultBelongsTo,
      isd,
    },
  });

  const onSubmit = async (data: z.infer<typeof ContactDetailsSchema>) => {
    try {
      const payload = {
        ...data,
        investment_account_guid: client?.investment_account ?? "",
        autofilled: false,
        autofill_source: null,
      };
      if (details) {
        await updateContactInfo(payload);
      } else {
        await createContactInfo(payload);
      }
      await updateClient({
        email: data.email,
      });
      nextStep();
    } catch (error) {
      handleError(error, "Failed to update contact information");
    }
  };
  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Contact Details</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="number"
            render={({ field }) => (
              <FormPrefixedInput
                prefix=""
                label="Mobile number"
                disabled
                type="tel"
                {...field}
              >
                {isd}
              </FormPrefixedInput>
            )}
          />
          <FormField
            control={form.control}
            name="phone_belongs_to"
            render={({ field }) => (
              <FormSelect
                options={BELONGS_TO}
                label="Mobile belongs to"
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => <FormInput label="Email" {...field} />}
          />
          <FormField
            control={form.control}
            name="email_belongs_to"
            render={({ field }) => (
              <FormSelect
                options={BELONGS_TO}
                label="Email belongs to"
                {...field}
              />
            )}
          />
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <div className="flex items-start p-3 border gap-2 border-border rounded-lg bg-tag-slate text-tag-slate-foreground">
              <InfoIcon className="size-4 stroke-tag-slate-foreground" />
              <span className="text-xs">
                Mark as self only if email and phone belongs to the client
              </span>
            </div>
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
